'use client'
import { Box, Link, Typography, styled } from '@mui/material'
import PaddedGrid from '@/UI/Layout/Grid/PaddedGrid'
import HeadingWithAnchor from '@/UI/Heading/HeadingWithAnchor'
import MuiAccordion from '@/UI/MUI/Accordion/Accordion'
import {
  whereWeCollectYourPersonalData,
  howWeUseYourPersonalData,
  howWeShareYourPersonalData,
  rightToAccessYourPersonalData,
  sensitiveInformation
} from './Content'

const ListItem = styled('ul')(() => ({}))

const PageContentAccordions = () => {
  return (
    <>
      <Typography variant="h1" align="center">
        Privacy Policy
      </Typography>

      <PaddedGrid columns={12}>
        {/* 1 About Zuto */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor title="1. About Zuto" sectionId="about-zuto" />
          <Typography variant="body1">
            Zuto Limited and its affiliates (collectively,{' '}
            <strong>“Zuto”, “we”, “us” or “our”</strong>) take your privacy and the security of your
            personal data seriously. This privacy policy is meant to help you understand what
            personal data we collect on you, how we use, share and protect such information and the
            rights and choices you have with regard to your personal data when you visit our website
            (www.zuto.com), when you communicate with us, or where you otherwise use our services.
          </Typography>
        </Box>

        {/* 2 Who is responsible for protecting your personal data */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="2. Who is responsible for protecting your personal data"
            sectionId="who-is-responsible-for-protecting-your-personal-data"
          />
          <Typography variant="body1">
            For the purposes of European Union and United Kingdom (<strong>“UK”</strong>) data
            protection laws, and depending on how you interact with our services, the responsible
            entity (i.e., controller) of your personal data will be one of the following entities:
            Zuto Limited, Zuto Business Services Limited, Zuto Holdings Limited and Zuto Ancillary
            Services Limited.
          </Typography>
          <Typography variant="body1">
            If you have any questions or complaints, please contact us at{' '}
            <Link href="mailto:datarequests@zuto.com">datarequests@zuto.com</Link>.
          </Typography>
        </Box>

        {/* 3 Where we collect your personal data from */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="3. Where we collect your personal data from"
            sectionId="where-we-collect-your-personal-data"
          />
          <Typography variant="body1">
            We collect most of the personal data we process directly from you (for example, when you
            complete an application or contact us). We may collect some personal data from third
            parties, including price comparison websites, credit reference agencies, and fraud
            prevention agencies as part of our services.
          </Typography>
          <MuiAccordion accordionData={whereWeCollectYourPersonalData} />
        </Box>

        {/* 4 What personal data we collect */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="4. What personal data we collect"
            sectionId="what-personal-data-we-collect"
          />
          <Typography variant="body1">
            When you make an application or otherwise engage with us, you voluntarily supply us with
            some of your personal data. This may include (but is not limited to) your contact
            information, employment details, financial information, identity information and
            marketing preferences. We also collect data that you voluntarily provide us with in
            communications that you have with us, data we collect automatically when you use our
            website, and data we receive from third parties. Sometimes we might also need to collect
            sensitive data about you.
          </Typography>
          <MuiAccordion accordionData={sensitiveInformation} />
        </Box>

        {/* 5 How and why we use your personal data */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="5. How and why we use your personal data"
            sectionId="how-and-why-we-use-your-personal-data"
          />
          <Typography variant="body1">
            We use your personal data for a variety of purposes described below. We process your
            personal data for these purposes in reliance on our legitimate business interests, in
            order to perform our contractual obligations to you or third parties with whom we have a
            contractual relationship (e.g. lenders or credit reference agencies), with your consent,
            and/or for compliance with our legal obligations. We indicate the specific processing
            grounds we rely on next to each purpose listed below.
          </Typography>

          <Typography
            variant="subHeading"
            sx={{ mb: (theme) => theme.spacing(6), display: 'block' }}
          >
            What we use your personal data for:
          </Typography>

          <MuiAccordion accordionData={howWeUseYourPersonalData} />
        </Box>

        {/*6 Who we share your data with and why */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="6. Who we share your data with and why"
            sectionId="who-we-share-your-data-with-and-why"
          />
          <Typography variant="body1">
            We may need to disclose your personal data to others to ensure we can efficiently
            provide the products, services, and information you request.
          </Typography>
          <Typography variant="body1">
            In particular, please note that when you make an application, we will share personal
            data you submit during the application process with our panel of lenders to process your
            application and generate a quote. Lenders may contact you directly and may share your{' '}
            personal data with fraud prevention agencies as part of assessing your application.
          </Typography>
          <Typography variant="body1">
            We also need to share your personal data with some of our suppliers, who act as
            processors on our behalf.
          </Typography>

          <MuiAccordion accordionData={howWeShareYourPersonalData} />
        </Box>

        {/* 7 It is important that you provide us with accurate information */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="7. It is important that you provide us with accurate information"
            sectionId="it-is-important-that-you-provide-us-with-accurate-information"
          />
          <Typography variant="body1">
            Where we need to collect personal data by law, or under the terms of a contract we have
            with you and you fail to provide that data when requested, we may not be able to perform
            the contract we have or are trying to enter into with you (for example, to provide you
            with financing or other services). In this case, we may have to cancel such financing or
            service you have with us, but we will notify you if this is the case at the time.
          </Typography>
        </Box>

        {/* 8.	Where we store your personal data */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="8. Where we store your personal data"
            sectionId="where-we-store-your-personal-data"
          />
          <Typography variant="body1">
            Your personal data provided in line with the purposes detailed in this privacy policy
            will mainly be stored on servers based in the UK. Please note that we may transfer your
            personal data to our service providers in jurisdictions outside the UK, such as the
            United States and other jurisdictions. Please note that such jurisdictions may not
            provide the same protections as the data protection laws in your home country.
          </Typography>
          <Typography variant="body1">
            When we engage in cross-border data transfers, we will ensure that relevant safeguards
            are in place to afford adequate protection for personal information and we will comply
            with applicable data protection laws, in particular by relying on an EU Commission or UK
            government adequacy decision or on contractual protections for the transfer of personal
            information. For more information about how we transfer personal information
            internationally, please contact us.
          </Typography>
        </Box>

        {/* 9.	How long we keep your personal data for */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="9. How long we keep your personal data for"
            sectionId="how-long-we-keep-your-personal-data-for"
          />
          <Typography variant="body1">
            We keep your personal data as long as necessary for the purposes set out in this notice
            and to comply with our legal and regulatory obligations.
          </Typography>
          <Typography variant="body1">
            As a guide: if you complete a finance application, we retain your data for 6 years after
            your agreement ends; incomplete applications are retained for 6 months; if you applied
            but didn&apos;t take out finance, your data is retained for up to 6 years. Marketing
            data is held until you opt out, after which we retain your details on a suppression list
            for 3 years.
          </Typography>
        </Box>

        {/* 10.	What rights you have over your personal data */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="10. What rights you have over your personal data"
            sectionId="what-rights-you-have-over-your-personal-data"
          />
          <Typography variant="body1">
            You have certain rights over your personal data. These include rights to access a copy
            of your personal data, to ask us to erase your personal data and ask us to correct
            inaccurate personal data. You can ask to exercise these rights by contacting us at{' '}
            <Link href="mailto:datarequests@zuto.com">datarequests@zuto.com</Link>. There are some
            circumstances in which we do not need to comply with all or part of your request. If
            this is the case, we will explain this to you.
          </Typography>
          <Typography variant="body1">
            The rights you have, and what each of these means, are explained in the table below. If
            you ask to exercise one of these rights, we may ask you to verify your identity before
            we process your request. This is to avoid confidentiality breaches and make sure we do
            not disclose personal data to the wrong person.
          </Typography>

          <MuiAccordion accordionData={rightToAccessYourPersonalData} />
        </Box>

        {/* 11.	Credit Reference Agencies */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="11. Credit Reference Agencies"
            sectionId="credit-reference-agencies"
          />
          <Typography variant="body1">
            We use CRAs to help us carry out credit and identity checks when you apply for a product
            or service with us. This involves us sharing your personal data with CRAs and receiving
            personal data back from them. We use the personal data they send us to assess our credit
            risk and make sure what you’ve told us is true.
          </Typography>
          <Typography variant="body1">
            We share your personal data with CRAs to ask them to provide a credit scoring
            computation when you make an application. Credit scoring uses several factors to assess
            risks involved in any application. A score is given to each factor and a total score
            obtained. Where automatic credit scoring computations are used, acceptance or rejection
            of your application will not depend only on the results of the credit scoring process.
          </Typography>
          <Typography variant="body1">
            When we ask CRAs about you, they will note it on your credit file. This is called a
            credit search. Other organisations (including lenders or providers of goods or services)
            will see this credit search or previous footprint on any report prepared for their own
            purposes and prospective relationship with you. The CRAs have created a “Credit
            Reference Agency Information Notice” or “CRAIN” which includes more details about how
            the CRAs use and share your personal data, as well as their role as fraud prevention
            agencies. The CRAINs for each of the three main CRAs are available on their websites,
            which we have linked below:
          </Typography>
          <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
            <li>
              Experian:{' '}
              <Link href="https://www.experian.co.uk/legal/crain/" role="link">
                https://www.experian.co.uk/legal/crain/
              </Link>
            </li>
            <li>
              Equifax:{' '}
              <Link href="https://www.equifax.co.uk/crain/" role="link">
                https://www.equifax.co.uk/crain/
              </Link>
            </li>
            <li>
              Transunion:{' '}
              <Link
                href="https://www.transunion.co.uk/legal/privacy-centre/pc-credit-reference"
                role="link"
              >
                https://www.transunion.co.uk/legal/privacy-centre/pc-credit-reference
              </Link>
            </li>
          </ListItem>
          <Typography variant="body1">
            When you request a quote, we conduct a soft credit search, which does not affect your
            credit score and is not visible to other lenders or organisations on your credit file.
            If you proceed with a full application, or to get a quote from certain lenders on our
            panel, a hard credit search will be conducted by the relevant lender, which will be
            recorded on your credit file and may be seen by other organisations. We will make this
            clear to you before any hard search is carried out.
          </Typography>
          <Typography variant="body1">
            You can also find more information about how the CRAs use personal data, and your data
            protection rights with the CRAs, here:{' '}
            <Link href="https://ico.org.uk/for-the-public/credit/" role="link">
              https://ico.org.uk/for-the-public/credit/
            </Link>
          </Typography>
        </Box>

        {/* 12.	Automated decision-making */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="12. Automated decision-making"
            sectionId="automated-decision-making"
          />
          <Typography variant="body1">
            Some decisions made in connection with your application involve automated processing.
            When you apply for car finance, we and/or our panel of lenders may use automated systems
            to assess your creditworthiness, including automated credit scoring. These automated
            checks consider factors such as your credit history, income, outgoings, and the details
            you provide in your application.
          </Typography>
          <Typography variant="body1">
            Please note that Zuto facilitates the broking of motor vehicle finance by matching
            customers to lenders. Automated credit decisions on applicants’ creditworthiness are
            made by our panel lenders, not by Zuto. If a lender makes an automated credit decision,
            please contact the lender.
          </Typography>
        </Box>

        {/* 13.	How we use your personal data for marketing */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="13. How we use your personal data for marketing"
            sectionId="how-we-use-your-personal-data-for-marketing"
          />
          <Typography variant="body1">
            We may send you marketing communications about our products and services, and those of
            our Zuto Partners. In certain instances, we may ask you for your explicit consent to
            market to you, but in other cases we may rely on our legitimate interests in promoting
            our business and our products and services. In each case you will be given the
            opportunity to opt-out in each marketing communication. You can unsubscribe at any time.
            You should be aware that if you opt out of marketing, you may continue to receive
            communications from us about your own products. These are called “service messages” and
            are not marketing communications.
          </Typography>
          <Typography variant="body1">We may:</Typography>
          <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
            <li>
              contact you by mail, telephone, email, SMS, or other electronic messaging service with
              offers of products, services or information that may be of interest to you; and/or
            </li>
            <li>
              send you information about goods and services provided by our Zuto Partners (please
              see below for more information on our Zuto Partners).
            </li>
          </ListItem>
          <Typography variant="body1">
            Before we send you any marketing communications, we will make sure that either:
          </Typography>
          <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
            <li>you have given us your consent;</li>
            <li>
              you have given consent to the company that shared your personal data with us, allowing
              Zuto to contact you for marketing purposes; or{' '}
            </li>
            <li>
              if we have collected your information whilst selling, or negotiating to sell, our
              products or services, we have given you the opportunity to opt out of receiving
              marketing from us.
            </li>
          </ListItem>
          <Typography variant="body1">
            Our Zuto Partners include third parties who provide products which may be relevant and
            helpful for you:
          </Typography>
          <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
            <li>
              <strong>Insurance comparison providers</strong> - this allows you to compare insurance
              products so you can make an informed decision.
            </li>
            <li>
              <strong>Vehicle maintenance & roadside assistance providers</strong> - this enables
              you to review complementary products that you may wish to purchase after financing a
              new vehicle.
            </li>
            <li>
              <strong>Credit score companies</strong> – to help you keep up to date with your credit
              score and improve it going forward.
            </li>
            <li>
              <strong>Alternative finance providers</strong> – if our main panel of lenders are
              unable to help you, we may be able to offer you some other alternatives, or present
              you with other products you may also be interested in.
            </li>
            <li>
              <strong>Car selling providers</strong> – to help you consider your options if you have
              a vehicle you wish to sell or part exchange.
            </li>
          </ListItem>
          <Typography variant="body1">
            We do not share your personal data with Zuto Partners, but we may send you information
            about their products and services if you have consented.
          </Typography>
          <Typography variant="body1">
            If you do not wish to receive marketing information from us, please{' '}
            <strong>DO NOT</strong> tick the relevant boxes when submitting your personal data by
            application, or on one of our sign up forms. Alternatively, please contact us using the
            details below or simply unsubscribe from any email that is sent to you by Zuto (which
            will be located at the bottom of the email).
          </Typography>
        </Box>

        {/* 14.	Use of Cookies  */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor title="14. Use of Cookies" sectionId="use-ofcookies" />
          <Typography variant="body1">
            Like most organisations, we use cookies on our website. Please see{' '}
            <Link href="https://www.zuto.com/cookies/">our cookies policy here</Link>
          </Typography>
        </Box>

        {/* 15.	Children  */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor title="15. Children" sectionId="children" />
          <Typography variant="body1">
            The services are not intended for use by children under 18 years of age. If we learn
            that we have collected personal data through our services from a child under 18 without
            the consent of the child’s parent or guardian as required by law, we will delete it as
            soon as possible.
          </Typography>
        </Box>

        {/* 16.	Questions or Complaints  */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="16. Questions or Complaints"
            sectionId="questions-or-complaints"
          />
          <Typography variant="body1">
            If you have any questions about this privacy notice or have a concern about how we have
            handled your personal information, we encourage you to contact us in the first instance
            so we can try to resolve it for you.
          </Typography>
          <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
            <li>
              Email: <Link href="mailto:datarequests@zuto.com">datarequests@zuto.com</Link>
            </li>
            <li>
              Telephone number: <Link href="tel:01625619944">01625 619944</Link>
            </li>
            <li>Post: Zuto, Winterton House, Winterton Way, Macclesfield. SK11 0LP.</li>
          </ListItem>
          <Typography variant="body1">
            We aim to acknowledge your complaint within 30 days and will keep you informed
            throughout the process.
          </Typography>
          <Typography variant="body1">
            For more details on how we handle complaints, please visit our complaints policy and{' '}
            <Link href="https://www.zuto.com/customer-care-and-complaints/">procedure here</Link>.
          </Typography>
          <Typography variant="body1">
            You also have the right to lodge a complaint directly with the Information
            Commissioner’s Office (ICO) at <Link href="https://www.ico.org.uk">www.ico.org.uk</Link>{' '}
            at any time.
          </Typography>
        </Box>

        {/* 17.	Changes to Privacy Policy */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="17. Changes to Privacy Policy"
            sectionId="changes-to-privacy-policy"
          />
          <Typography variant="body1">
            We reserve the right, at our discretion to change, modify, add, or remove portions of
            this policy at any time so you are encouraged to review this policy from time to time.
          </Typography>
        </Box>

        {/* 18.	Other Sites and Services */}
        <Box component="section" sx={{ mb: (theme) => theme.spacing(7) }}>
          <HeadingWithAnchor
            title="18. Other Sites and Services"
            sectionId="other-sites-and-services"
          />
          <Typography variant="body1">
            Our services may contain links to websites and other online services operated by third
            parties. In addition, our content may be included on web pages or online services that
            are not associated with us. These links and integrations are not an endorsement of, or
            representation that we are affiliated with, any third party. We do not control third
            party websites or online services, and we are not responsible for their actions. Other
            websites and services follow different rules regarding the collection, use and
            disclosure of your personal data. We encourage you to read the privacy policies of the
            other websites and online services you use.
          </Typography>
          <Typography variant="body1">
            The privacy notice was last updated on 17 July 2026.
          </Typography>
        </Box>
      </PaddedGrid>
    </>
  )
}

export default PageContentAccordions
